import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import TuneIcon from '@material-ui/icons/Tune';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import InputSwitch from '../../../../../components/Inputs/Switch';
import { SwitchSize } from '../../../../../components/Inputs/Switch/Switch';
import { useCompaniesDispatch } from '../../../shared/CompanyContext/CompanyContext';
import { CompanyContextActionTypes } from '../../../shared/CompanyContext/CompanyContext.types';
import {
  useFilterDispatch,
  useFilterState,
} from '../../../shared/FinancialDataContext/FinancialDataContext';
import { FinancialDataContextActionTypes } from '../../../shared/FinancialDataContext/FinancialDataContext.types';
import { FILTERS_WITH_HISTORY } from '../FilterLibrary/FilterDefinitions';
import AppliedFiltersItem from './AppliedFiltersItem';

const AppliedFilters: React.FC = React.memo(() => {
  const classes = useStyles();
  const dispatch = useFilterDispatch();
  const dispatchCompanies = useCompaniesDispatch();
  const { historySearch, appliedFilters } = useFilterState();
  const [searchInHistory, setSearchInHistory] = useState(!!historySearch);
  const [historyVisibility, setHistoryVisibility] = useState(false);
  const { t } = useTranslation();

  const renderedAppliedFilters = useMemo(() => {
    return (
      <>
        {appliedFilters.map((labelKey) => (
          <AppliedFiltersItem key={`filter-${labelKey}`} labelKey={labelKey} />
        ))}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  useEffect(() => {
    const isActiveHistoryBox = appliedFilters.some((labelKey) => {
      return FILTERS_WITH_HISTORY.indexOf(labelKey) !== -1;
    });
    setHistoryVisibility(isActiveHistoryBox);

    if (!appliedFilters.length) {
      dispatchCompanies({ type: CompanyContextActionTypes.ResetPagination });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const renderedSeachHistory = useMemo(() => {
    return (
      <Box className={`${classes.filterBox} searchBlock`}>
        <Typography className={classes.searchTypography}>
          {searchInHistory
            ? t(`companies:filters.seachGeneralOwnershipHistoryAllData`)
            : t(`companies:filters.seachGeneralOwnershipHistoryLocalData`)}
        </Typography>
        <InputSwitch
          fieldName={t(`companies:filters.searchInHistory`)}
          formControlClassName={classes.itemSwitch}
          size={SwitchSize.small}
          checked={searchInHistory}
          onChange={(value) => {
            setSearchInHistory(value);
            dispatch({
              type: FinancialDataContextActionTypes.UpdateHistorySearchValue,
              payload: value,
            });
          }}
        />
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInHistory]);

  return appliedFilters.length ? (
    <>
      {renderedAppliedFilters}
      {historyVisibility ? renderedSeachHistory : null}
    </>
  ) : (
    <Box className={classes.noDataBox}>
      <TuneIcon />
      <Typography>
        <Trans i18nKey="companies:noSelected" />
      </Typography>
      <Typography>
        <Trans i18nKey="companies:browserFilter" />
      </Typography>
    </Box>
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  filterBox: {
    margin: '5px 0 10px',
    padding: `0 ${theme.spacing(4)}px`,
    '& .MuiInputBase-input': {
      minHeight: '18px',
      height: '18px',
    },
    '& .MuiInputBase-root': {
      minHeight: '32px',
    },
    '&.searchBlock': {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: theme.palette.secondary.main,
      borderLeft: `${theme.spacing(2)}px solid ${theme.palette.secondary.dark}`,
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(6),
      paddingBottom: theme.spacing(2),
    },
  },
  searchTypography: {
    alignItems: 'center',
    display: 'flex',
    width: '80%',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    color: theme.palette.text.primary,
    fontSize: theme.typography.fontSize - 2,
  },
  itemSwitch: {
    width: '20%',
  },
  noDataBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    paddingTop: theme.spacing(13),
    color: theme.palette.secondary.dark,
    '& p': {
      margin: theme.spacing(4),
      fontSize: theme.typography.fontSize,
      fontWeight: 100,
    },
  },
}));

export default AppliedFilters;
